import apiClient from './client'

export interface LoginPayload {
  email: string
  password: string
  device?: string
}

export interface AuthUser {
  id: number
  name: string
  email: string
  role: string | null
  roles?: string[]
  permissions?: string[]
}

export interface LoginResponse {
  token: string
  expires_at: string
  user: AuthUser
}

export async function login(payload: LoginPayload): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/login', payload)
  localStorage.setItem('sanctum_token', data.token)
  localStorage.setItem('user', JSON.stringify(data.user))
  if (data.user.permissions) {
    localStorage.setItem('user_permissions', JSON.stringify(data.user.permissions))
  }
  if (data.user.roles) {
    localStorage.setItem('user_roles', JSON.stringify(data.user.roles))
  }
  // Cache lookups from login response — no more separate API calls needed
  if ((data as any).lookups) {
    localStorage.setItem('cached_lookups', JSON.stringify((data as any).lookups))
  }
  return data
}

/** Get cached lookups from login response */
export function getCachedLookups(): Record<string, any[]> | null {
  const raw = localStorage.getItem('cached_lookups')
  return raw ? JSON.parse(raw) : null
}

export function logout(): void {
  // Clear local state immediately — don't wait for API (can be slow)
  localStorage.removeItem('sanctum_token')
  localStorage.removeItem('user')
  localStorage.removeItem('user_permissions')
  localStorage.removeItem('user_roles')
  localStorage.removeItem('cached_lookups')
  // Fire-and-forget server logout
  apiClient.post('/auth/logout').catch(() => {})
}

export function getStoredPermissions(): string[] {
  const raw = localStorage.getItem('user_permissions')
  return raw ? JSON.parse(raw) : []
}

export function getStoredRoles(): string[] {
  const raw = localStorage.getItem('user_roles')
  return raw ? JSON.parse(raw) : []
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem('user')
  return raw ? JSON.parse(raw) : null
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem('sanctum_token')
}

// ── Policy cancel / reinstate authorisation (mirrors backend AuthGate) ──────
// Cancellation is product-scoped: Motor Comprehensive (product 3) vs the rest
// of the instant book [1,2,4,5,9]. Reinstatement uses `policy_reinstate`.
// Managers/Admins bypass. Fail-closed: no matching permission => no button
// (no permissive "empty perms = allow" fallback). The backend is the real gate;
// these only control button visibility.
const CANCEL_INSTANT_PRODUCT_IDS = [1, 2, 4, 5, 9]
const CANCEL_MOTOR_COMP_PRODUCT_ID = 3

export function isAdminOrManager(): boolean {
  return getStoredRoles().some((r) => /admin|manager/i.test(String(r)))
}

export function canReinstate(): boolean {
  return isAdminOrManager() || getStoredPermissions().includes('policy_reinstate')
}

export function canCancelProduct(productId: number): boolean {
  if (isAdminOrManager()) return true
  const perms = getStoredPermissions()
  if (productId === CANCEL_MOTOR_COMP_PRODUCT_ID) return perms.includes('policy_cancel_motor_comp')
  if (CANCEL_INSTANT_PRODUCT_IDS.includes(productId)) return perms.includes('policy_cancel_instant')
  return false
}

// OTP-signed Policy Cancellation Request form — a document action only, it
// never cancels cover (actual cancellation stays behind the existing cancel
// flow and its product-scoped permissions), so this is its own, deliberately
// broader permission. Fail-closed like canCancelProduct.
export function canGenerateCancellationForm(): boolean {
  return isAdminOrManager() || getStoredPermissions().includes('policy-cancellation-form-generate')
}

// The cancellation document on the KYC Documents tab (customer_kyc.Canceled_document)
// has its own explicit grant, cancel_document_upload, assigned from the Roles UI;
// cancelpolicyrequest_list (the Cancel Requests screen) has unlocked it since Sep 2026
// and still does. Either unlocks that one card and its "Submit cancellation document"
// button, with or without the full KYC grant. The backend recognises the same pair
// (PolicyCreateController::KYC_CANCELLATION_PERMISSIONS) plus the admin roles —
// mirrored here so the button is not shown to someone the backend would refuse.
const CANCELLATION_DOCUMENT_PERMISSIONS = [
  'cancel_document_upload',
  'cancelpolicyrequest_list',
]

// Exactly the roles AuthGate::ADMIN_ROLES lets through — not the looser
// "any role containing admin/manager" test above, which would light up the
// button for e.g. a Claims Manager the backend then refuses.
const KYC_CANCELLATION_ADMIN_ROLES = ['Super Admin', 'Manager', 'Admin']

export function canUploadCancellationDocument(): boolean {
  if (getStoredRoles().some((r) => KYC_CANCELLATION_ADMIN_ROLES.includes(String(r)))) return true
  const perms = getStoredPermissions()
  return CANCELLATION_DOCUMENT_PERMISSIONS.some((p) => perms.includes(p))
}

// Motor Comprehensive renew flow: the MIS Renew Policy list and the policy
// page's "Move to Renew" / "Renew" buttons. Strict — no admin/manager bypass,
// because the backend `permission:policy-renewal` middleware has none either;
// a bypass here would only show buttons that 403. `policy-renewal` is an
// existing row in the permissions table; roles get it from the Roles screen.
export function canRenewPolicy(): boolean {
  return getStoredPermissions().includes('policy-renewal')
}

/** Super Admin role, as stored at login. Mirrors backend `$user->hasRole('Super Admin')`. */
export function isSuperAdmin(): boolean {
  return getStoredRoles().some((r) => /super\s*admin/i.test(String(r)))
}

// Payment reversal / refund — sensitive money-movement. ONLY Super Admin
// bypasses (NOT the general admin/manager bypass); everyone else needs the
// `payment_reversal` permission. Mirrors backend AuthGate::canReversePayment.
export function canReversePayment(): boolean {
  return isSuperAdmin() || getStoredPermissions().includes('payment_reversal')
}
